import React, {useState} from "react";
import moment from "moment";
import {Box, Button, Flex, Progress, Text, useDisclosure} from "@chakra-ui/react";
import {BookingEntry, Week} from "../types";
import UpdateWorklog from "../../WorklogModals/UpdateWorklog";
import AddWorklogFromTimeTable from "../../WorklogModals/AddWorklogFromTimetable";
import {api} from "../../../api/timesheetAPI";
import {showSwal} from "../../../common_components/Alert";
import {useAppDispatch, useAppSelector} from "../../../stateManagement/hooks";
import {setTimeTableLoading, timetableRefresh} from "../../../stateManagement/refreshers";
import {getNLDateString, parseAPIDate} from "../../../dateUtils";
import {absenceBlockMessage, isAbsenceCodeBlocked} from "../WeekActions/absenceRestriction";
import {parseRawAmount} from "../WeekActions/hourPrecision";

interface MobileWeekViewProps {
    data: Week;
    detailed: boolean;
}

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ACCENT = "rgb(15 89 110)";

function fmt(hours: number): string {
    return Number.isInteger(hours) ? String(hours) : String(+hours.toFixed(2));
}

function statusColor(status: string | undefined): string {
    if (status === "approved" || status === "overtime") return "green.400";
    if (status === "not-approved") return "orange.400";
    return "border-subtle";
}

/** Day-by-day agenda replacing the 9-column week grid on phones. */
export function MobileWeekView({data, detailed}: MobileWeekViewProps) {
    const dates = data.week_dates.map(parseAPIDate);
    const todayStr = getNLDateString();
    const todayIdx = dates.findIndex(d => moment(d).format("YYYY-MM-DD") === todayStr);
    const [selected, setSelected] = useState<number>(todayIdx >= 0 ? todayIdx : 0);

    const dispatch = useAppDispatch();
    const impersonated = useAppSelector(state => state.impersonation.impersonatedUser);
    const currentUserName = useAppSelector(state => state.user.user.name);
    const updateModal = useDisclosure();
    const addModal = useDisclosure();
    const [updateData, setUpdateData] = useState<BookingEntry | null>(null);
    const [updateDay, setUpdateDay] = useState<number>();
    const [addDate, setAddDate] = useState<Date>(dates[0]);
    const [addJiraId, setAddJiraId] = useState<string>("");

    const timeOffText = (d: Date) => data.time_off[moment(d).format("YYYY-MM-DD")];
    const bookings = Object.entries(data.bookings || {});
    const day = dates[selected];
    const dayOffText = timeOffText(day);

    const logged = bookings.filter(([, b]) => b.story_booking_hours[selected] > 0);
    const others = bookings.filter(([, b]) => !(b.story_booking_hours[selected] > 0));

    function openEntry(key: string, booking: BookingEntry) {
        if (isAbsenceCodeBlocked(key, currentUserName)) {
            showSwal("failed", absenceBlockMessage(currentUserName));
            return;
        }
        setUpdateData(booking);
        setUpdateDay(selected);
        updateModal.onOpen();
    }

    function openAdd(jiraId: string) {
        if (jiraId && isAbsenceCodeBlocked(jiraId, currentUserName)) {
            showSwal("failed", absenceBlockMessage(currentUserName));
            return;
        }
        setAddDate(day);
        setAddJiraId(jiraId);
        addModal.onOpen();
    }

    function sendToErp() {
        const blocked = bookings
            .filter(([key, b]) => isAbsenceCodeBlocked(key, currentUserName)
                && b.story_booking_hours.some(h => parseRawAmount(h) > 0))
            .map(([key]) => key);
        if (blocked.length > 0) {
            showSwal("failed", `${absenceBlockMessage(currentUserName)}\n\nBlocked code(s): ${blocked.join(", ")}. Please use Edit to remove these entries first.`);
            return;
        }
        dispatch(setTimeTableLoading(true));
        api.getApproval({selected_date: moment(dates[0]).format("YYYY-MM-DD"), impersonate: impersonated})
            .then(() => {
                dispatch(timetableRefresh());
                showSwal("get-approval-success-message");
                dispatch(setTimeTableLoading(false));
            })
            .catch(reason => {
                showSwal("failed", reason.response.data);
                dispatch(setTimeTableLoading(false));
            });
    }

    const weekPct = data.expected_hours === 0 ? 100 : (data.total_weekly_hours / data.expected_hours) * 100;

    return (
        <Box className="tb-mobile-week" mb={2}>
            <UpdateWorklog isOpen={updateModal.isOpen} onClose={updateModal.onClose} initialData={updateData} idx_day={updateDay}/>
            <AddWorklogFromTimeTable isOpen={addModal.isOpen} onClose={addModal.onClose} date={addDate} jira_id={addJiraId}/>

            <Flex justify="space-between" align="baseline" mb={1}>
                <Text fontWeight={700} fontSize="sm">
                    Week {moment(dates[0]).isoWeek()}
                    <Text as="span" fontWeight={400} color="text-muted"> · {moment(dates[0]).format("MMM D")} – {moment(dates[6]).format("MMM D")}</Text>
                </Text>
                <Text fontWeight={700} fontSize="sm" color={data.total_weekly_hours >= data.expected_hours ? "green.500" : "text-muted"}>
                    {fmt(data.total_weekly_hours)} / {data.expected_hours.toFixed(1)}h
                    {data.total_overtime_hours > 0 && <Text as="span" fontStyle="italic" fontWeight={400}> +{data.total_overtime_hours}h PI</Text>}
                </Text>
            </Flex>
            <Progress value={weekPct} size="xs" borderRadius="full" mb={3}
                      colorScheme={data.total_weekly_hours >= data.expected_hours ? "green" : "pink"}/>

            <Flex gap={1} mb={3} role="tablist" aria-label="Days of the week">
                {dates.map((d, i) => {
                    const isSel = i === selected;
                    const isToday = i === todayIdx;
                    const weekend = d.getDay() === 0 || d.getDay() === 6;
                    const off = timeOffText(d) !== undefined;
                    const filled = isSel && isToday;
                    return (
                        <Box as="button" key={i} role="tab" aria-selected={isSel} onClick={() => setSelected(i)}
                             flex="1 1 0" minW={0} py={2} borderRadius="lg" borderWidth="1px" textAlign="center"
                             bg={filled ? ACCENT : isSel ? "gray.700" : isToday ? "blue.50" : weekend ? "orange.50" : "surface"}
                             _dark={{
                                 bg: filled ? ACCENT : isSel ? "gray.200" : isToday ? "rgba(66, 153, 225, 0.15)" : weekend ? "whiteAlpha.100" : "surface",
                                 color: filled ? "white" : isSel ? "gray.800" : "inherit",
                             }}
                             borderColor={isToday ? ACCENT : isSel ? "gray.700" : "border-subtle"}
                             boxShadow="none"
                             color={filled ? "white" : isSel ? "white" : isToday ? ACCENT : "inherit"}
                             transition="background-color .15s ease">
                            <Text fontSize="10px" fontWeight={600} textTransform="uppercase" opacity={0.75}>{DOW[i]}</Text>
                            <Text fontSize="md" fontWeight={700} lineHeight="1.2">{d.getDate()}</Text>
                            <Text fontSize="10px" fontWeight={600} opacity={isSel ? 0.9 : 0.7} mt="1px">
                                {off ? "off" : data.total_daily_hours[i] > 0 ? fmt(data.total_daily_hours[i]) : "–"}
                            </Text>
                        </Box>
                    );
                })}
            </Flex>

            <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight={700}>{moment(day).format("dddd, MMM D")}</Text>
                <Text fontSize="sm" color="text-muted">{fmt(data.total_daily_hours[selected])}h logged</Text>
            </Flex>

            {dayOffText && (
                <Box bg="surface-subtle" borderRadius="md" px={3} py={2} mb={2} fontSize="sm">{dayOffText}</Box>
            )}

            {logged.length === 0 && !dayOffText && (
                <Box borderWidth="1px" borderStyle="dashed" borderColor="border-subtle" borderRadius="lg" py={6} textAlign="center" mb={2}>
                    <Text color="text-muted" fontSize="sm">Nothing logged for this day</Text>
                </Box>
            )}

            {logged.map(([key, b]) => (
                <Flex as="button" key={key} onClick={() => openEntry(key, b)} w="100%" textAlign="left" align="center" gap={3}
                      mb={2} p={3} borderRadius="lg" borderWidth="1px" borderLeftWidth="4px" bg="surface"
                      borderColor="border-subtle" borderLeftColor={statusColor(b.status[selected])}
                      _active={{bg: "surface-muted"}}>
                    <Box flex="1" minW={0}>
                        <Text fontSize="xs" fontWeight={700} color="text-muted" letterSpacing="0.02em">{key}</Text>
                        <Text fontSize="sm" noOfLines={2}>{b.story_name}</Text>
                    </Box>
                    <Text fontSize="xl" fontWeight={700} flexShrink={0}>{fmt(b.story_booking_hours[selected])}<Text as="span" fontSize="xs" fontWeight={500} color="text-muted">h</Text></Text>
                </Flex>
            ))}

            <Button w="100%" variant="outline" borderStyle="dashed" onClick={() => openAdd("")} mb={3}>+ Add entry</Button>

            {others.length > 0 && (
                <Box mb={3}>
                    <Text fontSize="xs" fontWeight={700} color="text-muted" textTransform="uppercase" mb={1}>Log on another task from this week</Text>
                    <Flex gap={2} flexWrap="wrap">
                        {others.map(([key, b]) => (
                            <button key={key} className="tb-pill tb-pill--sm" title={b.story_name} onClick={() => openAdd(key)}>
                                + {key}
                            </button>
                        ))}
                    </Flex>
                </Box>
            )}

            {detailed && (
                <Button w="100%" colorScheme="orange" onClick={sendToErp}>Send To ERP system</Button>
            )}
        </Box>
    );
}
